import { MessageOutlined, CloseOutlined, SendOutlined } from "@ant-design/icons";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import '../assets/styles/support.css';
import axios from "axios";
import { END_POINT } from "config/environment";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { Tooltip } from "antd";

interface SupportChatProps {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Message {
    id: number | string;
    message: string;
    sender: 'user' | 'assistant';
    created_at: string;
}

const POLL_INTERVAL_MS = 3000;

export const SupportChat: FC<SupportChatProps> = ({ isOpen, setIsOpen }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const { token, userInfo } = useGetUserInfo();
    const [socketConnected, setSocketConnected] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const echoRef = useRef<Echo<any> | null>(null);
    const [isAssistantReplying, setIsAssistantReplying] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const isOpenRef = useRef(isOpen);

    const user = userInfo?.id;

    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    const upsertMessage = useCallback((incoming: Message) => {
        if (!incoming?.message) return;

        setMessages((prev) => {
            const exists = prev.some(
                (msg) =>
                    String(msg.id) === String(incoming.id) ||
                    (msg.sender === incoming.sender &&
                        msg.message === incoming.message &&
                        Math.abs(
                            new Date(msg.created_at).getTime() -
                                new Date(incoming.created_at).getTime(),
                        ) < 5000),
            );
            if (exists) {
                return prev.map((msg) =>
                    String(msg.id).startsWith('temp-') &&
                    msg.sender === incoming.sender &&
                    msg.message === incoming.message
                        ? incoming
                        : msg,
                );
            }
            return [...prev, incoming];
        });

        if (incoming.sender === 'assistant') {
            setIsAssistantReplying(false);
            if (!isOpenRef.current || isMinimized) {
                setUnreadCount((prev) => prev + 1);
            }
        }
    }, [isMinimized]);

    const getMessages = useCallback(async () => {
        if (!token) return;

        try {
            const response = await fetch(`${END_POINT.BASE_URL}/v1/support`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
            });
            const data = await response.json();
            const list = Array.isArray(data) ? data : [];
            setMessages(
                list.map((item: any) => ({
                    id: item.id,
                    message: item.message,
                    sender: item.sender === 'user' ? 'user' : 'assistant',
                    created_at: item.created_at,
                })),
            );
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    }, [token]);

    const leaveChannel = () => {
        if (echoRef.current && user) {
            echoRef.current.leave(`support.${user}`);
        }
    };

    const connectToChannel = useCallback(() => {
        if (!token || !user) return;

        window.Pusher = Pusher as any;

        if (!echoRef.current) {
            const reverbKey =
                process.env.REACT_APP_REVERB_APP_KEY || "1f5b0a0c1b28fca642d4";
            const reverbHost =
                process.env.REACT_APP_REVERB_HOST || window.location.hostname;
            const reverbPort = process.env.REACT_APP_REVERB_PORT || "8080";
            const isSecure =
                window.location.protocol === "https:" ||
                process.env.REACT_APP_REVERB_SCHEME === "https";

            echoRef.current = new Echo({
                broadcaster: 'reverb',
                key: reverbKey,
                wsHost: reverbHost,
                wsPort: Number(reverbPort),
                wssPort: 443,
                forceTLS: isSecure,
                enabledTransports: isSecure ? ['wss'] : ['ws'],
                authorizer: (channel: { name: string }) => ({
                    authorize: (
                        socketId: string,
                        callback: (error: boolean, data: any) => void,
                    ) => {
                        axios
                            .post(
                                `${END_POINT.AUTH_URL}/broadcasting/auth`,
                                {
                                    socket_id: socketId,
                                    channel_name: channel.name,
                                },
                                { headers: { Authorization: `Bearer ${token}` } },
                            )
                            .then((response) => callback(false, response.data))
                            .catch((error) => {
                                setSocketConnected(false);
                                callback(true, error);
                            });
                    },
                }),
            });

            echoRef.current.connector.pusher.connection.bind('connected', () => {
                setSocketConnected(true);
            });
            echoRef.current.connector.pusher.connection.bind('disconnected', () => {
                setSocketConnected(false);
            });
            echoRef.current.connector.pusher.connection.bind('unavailable', () => {
                setSocketConnected(false);
            });
        }

        leaveChannel();

        echoRef.current
            .private(`support.${user}`)
            .listen('.SupportSent', (event: any) => {
                const payload = event?.support ?? event;
                upsertMessage({
                    id: payload?.id || `ws-${Date.now()}`,
                    message: payload?.message || '',
                    sender: payload?.sender === 'user' ? 'user' : 'assistant',
                    created_at: payload?.created_at || new Date().toISOString(),
                });
            });
    }, [token, user, upsertMessage]);

    useEffect(() => {
        if (!isOpen || !token || !user) return;

        connectToChannel();
        getMessages();
        setUnreadCount(0);

        return () => {
            leaveChannel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, token, user]);

    // Poll while waiting for an assistant reply (websocket fallback)
    useEffect(() => {
        if (!isOpen || !token || !isAssistantReplying) return;

        const timer = window.setInterval(() => {
            getMessages();
        }, POLL_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [isOpen, token, isAssistantReplying, getMessages]);

    // After a poll refresh, stop the typing indicator if assistant already replied
    useEffect(() => {
        if (!isAssistantReplying || messages.length === 0) return;
        const last = messages[messages.length - 1];
        if (last?.sender === 'assistant') {
            setIsAssistantReplying(false);
        }
    }, [messages, isAssistantReplying]);

    useEffect(() => {
        return () => {
            if (echoRef.current) {
                echoRef.current.disconnect();
                echoRef.current = null;
            }
        };
    }, []);

    const handleSendMessage = async () => {
        if (!inputValue.trim() || !user || !token || isAssistantReplying) return;

        const text = inputValue.trim();
        const tempId = `temp-${Date.now()}`;
        const tempMessage: Message = {
            id: tempId,
            message: text,
            sender: 'user',
            created_at: new Date().toISOString(),
        };

        try {
            setMessages((prev) => [...prev, tempMessage]);
            setInputValue('');
            setIsAssistantReplying(true);

            const { data } = await axios.post(
                `${END_POINT.BASE_URL}/v1/sendsupport`,
                { user_id: user, text },
                { headers: { Authorization: `Bearer ${token}` } },
            );

            // Prefer HTTP response so reply appears even if websockets fail
            if (data?.user_message) {
                upsertMessage({
                    id: data.user_message.id,
                    message: data.user_message.message,
                    sender: 'user',
                    created_at: data.user_message.created_at,
                });
            }

            if (data?.assistant_message) {
                upsertMessage({
                    id: data.assistant_message.id,
                    message: data.assistant_message.message,
                    sender: 'assistant',
                    created_at: data.assistant_message.created_at,
                });
            } else {
                // Fallback: refresh history if response shape is unexpected
                await getMessages();
                setIsAssistantReplying(false);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
            setIsAssistantReplying(false);
        }
    };

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        }
        if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        }
        return date.toLocaleDateString();
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isAssistantReplying]);

    if (!isOpen) {
        return (
            <div
                className="fixed bottom-4 right-4 w-16 h-16 bg-primary rounded-full shadow-lg flex items-center justify-center cursor-pointer z-[1000] hover:bg-blue-700 transition-colors"
                onClick={() => setIsOpen(true)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="relative">
                    <MessageOutlined className="text-white text-xl" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {unreadCount}
                        </span>
                    )}
                    {isHovered && (
                        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                            Chat with Support
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (isMinimized) {
        return (
            <div className="fixed bottom-4 right-[72px] w-64 bg-white shadow-lg rounded-t-lg border border-gray-200 z-[1000]">
                <div
                    className="bg-primary text-white p-3 rounded-t-lg flex justify-between items-center cursor-pointer"
                    onClick={() => {
                        setIsMinimized(false);
                        setUnreadCount(0);
                    }}
                >
                    <div className="flex items-center gap-2">
                        <MessageOutlined />
                        <span className="font-semibold">Arno Support</span>
                    </div>
                    {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {unreadCount}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="fixed bottom-4 right-[72px] w-80 h-[500px] bg-white shadow-xl rounded-lg flex flex-col border border-gray-200 z-[1000] transition-all duration-300">
            <div className="bg-primary text-white p-3 rounded-t-lg flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className="font-semibold">Arno Support</span>
                    {socketConnected ? (
                        <span className="h-2 w-2 bg-green-400 rounded-full ml-2"></span>
                    ) : (
                        <span className="h-2 w-2 bg-red-400 rounded-full ml-2" title="Realtime sync unavailable; replies still load via API"></span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Tooltip title="Minimize">
                        <button
                            className="text-white hover:text-gray-200 focus:outline-none"
                            onClick={() => setIsMinimized(true)}
                        >
                            <span className="text-xs">_</span>
                        </button>
                    </Tooltip>
                    <Tooltip title="Close">
                        <CloseOutlined
                            className="cursor-pointer hover:text-gray-200"
                            onClick={() => setIsOpen(false)}
                        />
                    </Tooltip>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 chat-messages">
                {messages.length === 0 && !isAssistantReplying && (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                        <MessageOutlined className="text-4xl mb-2" />
                        <p className="text-center">How can we help you today?</p>
                        <p className="text-sm text-center mt-1">Our support team is ready to assist you</p>
                    </div>
                )}

                {messages.map((msg, index) => {
                    const showDateSeparator =
                        index === 0 ||
                        formatDate(messages[index - 1].created_at) !==
                            formatDate(msg.created_at);

                    return (
                        <div key={msg.id}>
                            {showDateSeparator && (
                                <div className="text-center my-3">
                                    <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded">
                                        {formatDate(msg.created_at)}
                                    </span>
                                </div>
                            )}
                            <div
                                className={`mb-3 flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[80%] p-3 rounded-lg ${
                                        msg.sender === 'user'
                                            ? 'bg-blue-500 text-white rounded-br-none'
                                            : 'bg-gray-200 text-gray-800 rounded-bl-none'
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        <div className="flex-1">
                                            <div className="text-sm whitespace-pre-wrap">
                                                {msg.message}
                                            </div>
                                            <div
                                                className={`text-xs mt-1 ${
                                                    msg.sender === 'user'
                                                        ? 'text-blue-100'
                                                        : 'text-gray-500'
                                                }`}
                                            >
                                                {formatTime(msg.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {isAssistantReplying && (
                    <div className="mb-3 flex justify-start">
                        <div className="max-w-[80%] p-3 rounded-lg bg-gray-200 text-gray-800 rounded-bl-none">
                            <div className="flex items-center gap-2">
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className="border-t p-3 bg-white">
                <div className="flex gap-2 items-center">
                    <input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        onKeyPress={(e) =>
                            e.key === 'Enter' &&
                            inputValue.trim() &&
                            !isAssistantReplying &&
                            handleSendMessage()
                        }
                        disabled={isAssistantReplying}
                        autoFocus
                    />
                    <button
                        disabled={!inputValue.trim() || isAssistantReplying}
                        onClick={handleSendMessage}
                        className={`rounded-lg w-10 h-10 flex items-center justify-center transition-colors ${
                            inputValue.trim() && !isAssistantReplying
                                ? 'bg-primary text-white hover:bg-blue-600'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        <SendOutlined />
                    </button>
                </div>
                <div className="text-xs text-gray-400 mt-1 text-center">
                    Typically replies within a few minutes
                </div>
            </div>
        </div>
    );
};
